import {
    Box,
    Button,
    Menu,
    MenuItem,
    MenuProps,
    alpha,
    styled,
} from '@mui/material';
import GatewayItem from './gateway-item';
import { useGatewaysQuery, useUserProfileQuery } from '../../../../app/store';
import { Gateway } from '../../../../types';
import { useDispatch } from 'react-redux';
import {
    setActiveGateway,
    visibleCreateDialog,
} from '../../../../app/store/gateway.slice';
import { useLocation, useNavigate } from 'react-router-dom';
import { getGatewayAccess } from 'features/gateways/gateway-access.utils';
import { getGatewaySwitchPath } from './gateway-switch-path';

const StyledMenu = styled((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }: any) => ({
    '& .MuiPaper-root': {
        borderRadius: '16px',
        minWidth: 230,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100dvh - 16px)',
        overflow: 'hidden',
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.border.light}`,
        boxShadow: theme.palette.mode === 'light' ? '0 22px 55px rgba(23,32,51,.15)' : '0 22px 55px rgba(0,0,0,.42)',
        '& .MuiMenu-list': {
            padding: '6px',
            overflow: 'hidden',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
    },
}));

type GatewaySelectorMenu = {
    open: boolean;
    handleClose: () => void;
    anchorEl: null | HTMLElement;
};

const GatewaySelectorMenu: React.FC<GatewaySelectorMenu> = ({
    anchorEl,
    handleClose,
    open,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { data: gateways } = useGatewaysQuery({ page: 1 });
    const { data: profile } = useUserProfileQuery();

    return (
        <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <Box
                sx={(theme) => ({
                    maxHeight:
                        (gateways?.items?.length ?? 0) > 5 ? 240 : 'none',
                    overflowY:
                        (gateways?.items?.length ?? 0) > 5 ? 'auto' : 'visible',
                    overflowX: 'hidden',
                    overscrollBehavior: 'contain',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '& > .MuiMenuItem-root': {
                        minHeight: 48,
                    },
                    '&::-webkit-scrollbar': {
                        display: 'none',
                        width: 0,
                        height: 0,
                    },
                })}>
                {gateways?.items?.map((gateway: Gateway) => {
                    const { canEnterGateway } = getGatewayAccess(
                        gateway,
                        profile?.id,
                    );
                    const isDisabled =
                        gateway.status !== 'activated' || !canEnterGateway;

                    return (
                        <MenuItem
                            key={gateway.id}
                            onClick={handleClose}
                            disabled={isDisabled}>
                            <GatewayItem
                                onClick={() => {
                                    if (isDisabled) {
                                        return;
                                    }

                                    const targetPath = getGatewaySwitchPath(
                                        pathname,
                                        gateways?.items ?? [],
                                        gateway,
                                        profile?.id,
                                    );

                                    if (targetPath !== pathname) {
                                        navigate(targetPath);
                                        return;
                                    }

                                    dispatch(setActiveGateway(gateway));
                                }}
                                collapse={true}
                                title={gateway.title}
                                website={gateway.website}
                                status={gateway.status}
                                style={{ color: 'text.primary' }}
                            />
                        </MenuItem>
                    );
                })}
            </Box>

            <Box
                onClick={handleClose}
                sx={{
                    p: 1,
                    backgroundColor: 'background.paper',
                    borderTop:
                        gateways && gateways?.items?.length > 0
                            ? '1px solid'
                            : 'none',
                    borderColor: 'border.main',
                }}>
                <Button
                    onClick={() => {
                        dispatch(visibleCreateDialog());
                    }}
                    fullWidth
                    variant="contained">
                    ایجاد درگاه پرداخت
                </Button>
            </Box>
        </StyledMenu>
    );
};

export default GatewaySelectorMenu;
